import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  Alert,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import {
  usePendingApprovalsQuery,
  useBatchApproveMutation,
  useApproveDeletionMutation,
  useRejectDeletionMutation,
} from '../../hooks/queries';
import { ChangeRequestList } from '../../types';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, EmptyState, PendingApprovalsSkeleton } from '../../components';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useAuth } from '../../contexts/AuthContext';
import { MainStackParamList } from '../../navigation/MainNavigator';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

interface PendingCounts {
  goals: number;
  initiatives: number;
  kpis: number;
  processes: number;
  sub_processes: number;
  services: number;
  activities: number;
  unit_goals: number;
}

interface TypeConfig {
  key: keyof PendingCounts;
  labelAr: string;
  labelEn: string;
  color: string;
  iconPath: string;
}

const TYPE_CONFIGS: TypeConfig[] = [
  {
    key: 'goals',
    labelAr: 'الأهداف الاستراتيجية',
    labelEn: 'Strategic Goals',
    color: tokens.colors.semantic.general["Main Color"],
    iconPath: 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  },
  {
    key: 'initiatives',
    labelAr: 'المبادرات',
    labelEn: 'Initiatives',
    color: tokens.colors.raw.blue["500"],
    iconPath: 'M13 10V3L4 14h7v7l9-11h-7z',
  },
  {
    key: 'kpis',
    labelAr: 'مؤشرات الأداء',
    labelEn: 'KPIs',
    color: tokens.colors.raw.green["500"],
    iconPath: 'M3 3v18h18M9 17V9m4 8V5m4 12v-4',
  },
  {
    key: 'processes',
    labelAr: 'العمليات',
    labelEn: 'Processes',
    color: '#8B5CF6',
    iconPath: 'M4 6h16M4 12h16M4 18h16',
  },
  {
    key: 'sub_processes',
    labelAr: 'العمليات الفرعية',
    labelEn: 'Sub-Processes',
    color: '#A855F7',
    iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  },
  {
    key: 'services',
    labelAr: 'الخدمات',
    labelEn: 'Services',
    color: '#F59E0B',
    iconPath: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  },
  {
    key: 'activities',
    labelAr: 'الأنشطة',
    labelEn: 'Activities',
    color: '#EC4899',
    iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  },
  {
    key: 'unit_goals',
    labelAr: 'أهداف الوحدات التنظيمية',
    labelEn: 'Unit Goals',
    color: '#06B6D4',
    iconPath: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  },
];

// Back Arrow Icon
const ArrowRightIcon = ({ size = 24, color = tokens.colors.raw.gray["900"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 18L15 12L9 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Checkmark icon for approve button
const CheckAllIcon = ({ size = 24, color = tokens.colors.raw.white }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

export default function PendingApprovalsScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const { user, isLoading: authLoading } = useAuth();
  const isRTL = language === 'ar';

  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
  };

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const successTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [successCounts, setSuccessCounts] = useState<PendingCounts | null>(null);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  // IDs already approved/rejected this session — hidden immediately so a
  // settled card can't be tapped again before the list refetch lands (the
  // second tap would 400 "not pending" and look like a failure).
  const [resolvedIds, setResolvedIds] = useState<Set<number>>(new Set());

  const departmentId = user?.profile?.organization;
  const userType = user?.profile?.user_type;
  const canApprove = userType === 'org_executive' || userType === 'superuser';
  // On a hard reload auth re-validates async, so `user` is briefly null and
  // canApprove false. Only treat the user as unauthorized once auth settles —
  // otherwise we'd redirect / hide the page mid-load.
  const authSettled = !authLoading && !!user;
  const isUnauthorized = authSettled && !canApprove;

  // Redirect genuinely-unauthorized users (org_employee, executive,
  // strategy_team) — but only after auth has settled.
  useEffect(() => {
    if (isUnauthorized) {
      navigation.goBack();
    }
  }, [isUnauthorized, navigation]);

  // React Query owns the approvals + deletion-requests fetch. Mutations
  // invalidate the queryKey on success, so the list refreshes automatically
  // — no manual `await fetchPendingApprovals()` threading needed.
  const {
    data: pendingBundle,
    isPending: loading,
    isRefetching: refreshing,
    refetch,
  } = usePendingApprovalsQuery(canApprove);

  const pendingCounts = useMemo<PendingCounts>(() => {
    const data = (pendingBundle?.approvals as any) ?? {};
    return {
      goals: data.strategic_goals?.length ?? data.goals_count ?? 0,
      initiatives: data.initiatives?.length ?? data.initiatives_count ?? 0,
      kpis: data.kpis?.length ?? data.kpis_count ?? 0,
      processes: data.processes?.length ?? data.processes_count ?? 0,
      sub_processes: data.sub_processes?.length ?? data.sub_processes_count ?? 0,
      services: data.services?.length ?? data.services_count ?? 0,
      activities: data.activities?.length ?? data.activities_count ?? 0,
      unit_goals: data.unit_goals?.length ?? data.unit_goals_count ?? 0,
    };
  }, [pendingBundle]);

  const deletionRequests: ChangeRequestList[] = (pendingBundle?.deletionRequests ?? []).filter(
    (req) => !resolvedIds.has(req.id),
  );
  const totalPending = Object.values(pendingCounts).reduce((sum, count) => sum + count, 0);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  const onRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const batchApproveMutation = useBatchApproveMutation();
  const approveDeletionMutation = useApproveDeletionMutation();
  const rejectDeletionMutation = useRejectDeletionMutation();
  const approving = batchApproveMutation.isPending;

  const handleBatchApprove = async () => {
    if (!departmentId) return;
    setShowConfirmModal(false);
    const prevCounts = { ...pendingCounts };
    try {
      await batchApproveMutation.mutateAsync(departmentId);
      setSuccessCounts(prevCounts);
      setShowSuccessMessage(true);
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => setShowSuccessMessage(false), 4000);
    } catch (error: any) {
      console.error('Error batch approving:', error);
      const msg = error?.response?.data?.detail || error?.response?.data?.message || (isRTL ? 'حدث خطأ أثناء الاعتماد' : 'Error during approval');
      Alert.alert(isRTL ? 'خطأ' : 'Error', msg);
    }
  };

  const markResolved = (id: number) =>
    setResolvedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

  // A 400 means the request is no longer pending (already approved/rejected),
  // i.e. our first tap already succeeded — treat as a benign no-op, not an error.
  const isAlreadyResolved = (error: any) => error?.response?.status === 400;

  const handleApproveDeletion = async (id: number) => {
    // Guard against re-tapping a card that's already settled or in flight.
    if (approvingId === id || resolvedIds.has(id)) return;
    setApprovingId(id);
    try {
      await approveDeletionMutation.mutateAsync(id);
      markResolved(id);
    } catch (error: any) {
      if (isAlreadyResolved(error)) {
        markResolved(id);
      } else {
        console.error('Error approving deletion:', error);
        const msg = error?.response?.data?.detail || (isRTL ? 'حدث خطأ أثناء الموافقة' : 'Error during approval');
        Alert.alert(isRTL ? 'خطأ' : 'Error', msg);
      }
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectDeletion = async (id: number) => {
    if (rejectingId === id || resolvedIds.has(id)) return;
    setRejectingId(id);
    try {
      await rejectDeletionMutation.mutateAsync({
        id,
        reason: isRTL ? 'مرفوض' : 'Rejected',
      });
      markResolved(id);
    } catch (error: any) {
      if (isAlreadyResolved(error)) {
        markResolved(id);
      } else {
        console.error('Error rejecting deletion:', error);
        const msg = error?.response?.data?.detail || (isRTL ? 'حدث خطأ أثناء الرفض' : 'Error during rejection');
        Alert.alert(isRTL ? 'خطأ' : 'Error', msg);
      }
    } finally {
      setRejectingId(null);
    }
  };

  const activeTypes = TYPE_CONFIGS.filter((config) => pendingCounts[config.key] > 0);

  // Unauthorized (auth settled, no approve rights) — keep the chrome/drawer
  // and show a message instead of a bare full-screen spinner.
  if (isUnauthorized) {
    return (
      <PageContainer>
        <Navbar />
        <EmptyState
          title={isRTL ? 'غير مصرح' : 'Not Authorized'}
          message={isRTL ? 'ليس لديك صلاحية للوصول إلى هذه الصفحة' : 'You do not have permission to access this page'}
        />
      </PageContainer>
    );
  }

  // Auth still validating, or first data load — render the full chrome (so the
  // drawer/navbar stay visible on hard reload) with a skeleton body.
  if (!authSettled || (loading && !pendingBundle)) {
    return (
      <PageContainer>
        <Navbar />
        <PendingApprovalsSkeleton />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <Breadcrumb
        items={[
          { label: isRTL ? 'الرئيسية' : 'Home' },
          { label: isRTL ? 'الموافقات المعلقة' : 'Pending Approvals' },
        ]}
      />

      {/* Header */}
      <View style={[styles.header, { backgroundColor: themeColors.card, borderBottomColor: themeColors.cardBorder, flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
        <View style={styles.headerContent}>
          <Text style={[styles.headerTitle, { color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }]}>
            {isRTL ? 'الموافقات المعلقة' : 'Pending Approvals'}
          </Text>
          <Text style={[styles.headerSubtitle, { color: themeColors.textMuted, textAlign: isRTL ? 'right' : 'left' }]}>
            {totalPending} {isRTL ? 'عنصر بانتظار الاعتماد' : 'items pending approval'}
          </Text>
        </View>
        <TouchableOpacity
          style={[styles.backButton, { backgroundColor: isDark ? colors.backgroundAlt : tokens.colors.raw.gray["100"] }]}
          onPress={() => navigation.goBack()}
        >
          <ArrowRightIcon size={24} color={themeColors.textPrimary} />
        </TouchableOpacity>
      </View>

      {/* Success Message */}
      {showSuccessMessage && successCounts && (
        <View style={[styles.successBanner, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
          <CheckAllIcon size={20} color="#16A34A" />
          <Text style={styles.successText}>
            {t('approval.batchApproveSuccess')}
          </Text>
        </View>
      )}

      {/* Content */}
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
      >
        {totalPending === 0 ? (
          <EmptyState
            title={t('approval.noPendingRequests')}
            message={isRTL ? 'تمت معالجة جميع الطلبات' : 'All requests have been processed'}
          />
        ) : (
          <>
            {/* Summary Cards */}
            {activeTypes.map((config) => {
              const count = pendingCounts[config.key];
              return (
                <View
                  key={config.key}
                  style={[
                    styles.typeCard,
                    {
                      backgroundColor: themeColors.card,
                      borderColor: themeColors.cardBorder,
                      flexDirection: isRTL ? 'row-reverse' : 'row',
                    },
                  ]}
                >
                  <View style={[styles.typeIconContainer, { backgroundColor: config.color + '14' }]}>
                    <Svg width={22} height={22} viewBox="0 0 24 24" fill="none">
                      <Path
                        d={config.iconPath}
                        stroke={config.color}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </Svg>
                  </View>
                  <View style={[styles.typeInfo, { alignItems: isRTL ? 'flex-end' : 'flex-start' }]}>
                    <Text style={[styles.typeLabel, { color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }]}>
                      {isRTL ? config.labelAr : config.labelEn}
                    </Text>
                    <Text style={[styles.typeSubLabel, { color: themeColors.textMuted, textAlign: isRTL ? 'right' : 'left' }]}>
                      {t('approval.pendingApproval')}
                    </Text>
                  </View>
                  <View style={[styles.countBadge, { backgroundColor: config.color + '14' }]}>
                    <Text style={[styles.countText, { color: config.color }]}>
                      {count}
                    </Text>
                  </View>
                </View>
              );
            })}

            {/* Total summary */}
            <View style={[styles.totalCard, { backgroundColor: isDark ? colors.backgroundAlt : tokens.colors.raw.indigo["50"], borderColor: COLORS.primary + '30', flexDirection: isRTL ? 'row-reverse' : 'row', justifyContent: 'space-between', alignItems: 'center' }]}>
              <Text style={[styles.totalLabel, { color: themeColors.textPrimary }]}>
                {isRTL ? 'إجمالي العناصر المعلقة' : 'Total Pending Items'}
              </Text>
              <Text style={[styles.totalCount, { color: COLORS.primary }]}>
                {totalPending}
              </Text>
            </View>
          </>
        )}

        {/* Deletion Requests Section */}
        {deletionRequests.length > 0 && (
          <View style={styles.deletionSection}>
            <Text style={[styles.deletionSectionTitle, { color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }]}>
              {isRTL ? 'طلبات الحذف' : 'Deletion Requests'}
            </Text>
            <Text style={[styles.deletionSectionSubtitle, { color: themeColors.textMuted, textAlign: isRTL ? 'right' : 'left' }]}>
              {deletionRequests.length} {isRTL ? 'طلب بانتظار الموافقة' : 'requests pending approval'}
            </Text>

            {deletionRequests.map((request) => (
              <View
                key={request.id}
                style={[styles.deletionCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
              >
                <View style={[styles.deletionCardHeader, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                  <View style={styles.deletionIconContainer}>
                    <Svg width={18} height={18} viewBox="0 0 24 24" fill="none">
                      <Path d="M3 6H5H21" stroke={tokens.colors.raw.red["500"]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={tokens.colors.raw.red["500"]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </Svg>
                  </View>
                  <View style={[styles.deletionCardInfo, { alignItems: isRTL ? 'flex-end' : 'flex-start' }]}>
                    <Text style={[styles.deletionCardTitle, { color: themeColors.textPrimary }]} numberOfLines={1}>
                      {request.object_title}
                    </Text>
                    <Text style={[styles.deletionCardMeta, { color: themeColors.textMuted }]}>
                      {request.content_type_display} • {request.requested_by_name || (isRTL ? 'مستخدم' : 'User')}
                    </Text>
                  </View>
                </View>
                <View style={[styles.deletionCardActions, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                  <Button
                    variant="destructive"
                    size="sm"
                    onPress={() => handleRejectDeletion(request.id)}
                    loading={rejectingId === request.id}
                  >
                    {isRTL ? 'رفض' : 'Reject'}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onPress={() => handleApproveDeletion(request.id)}
                    loading={approvingId === request.id}
                  >
                    {isRTL ? 'موافقة' : 'Approve'}
                  </Button>
                </View>
              </View>
            ))}
          </View>
        )}
      </ScrollView>

      {/* Approve All Button */}
      {totalPending > 0 && canApprove && (
        <View style={[styles.bottomBar, { backgroundColor: themeColors.card, borderTopColor: themeColors.cardBorder }]}>
          <Button
            variant="primary"
            onPress={() => setShowConfirmModal(true)}
            loading={approving}
            fullWidth
            leadingIcon={<CheckAllIcon size={22} color={tokens.colors.raw.white} />}
          >
            {t('approval.batchApprove')}
          </Button>
        </View>
      )}

      {/* Confirmation Modal */}
      <Modal
        visible={showConfirmModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowConfirmModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: themeColors.card }]}>
            <Text style={[styles.modalTitle, { color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }]}>
              {t('approval.batchApprove')}
            </Text>
            <Text style={[styles.modalMessage, { color: themeColors.textMuted, textAlign: isRTL ? 'right' : 'left' }]}>
              {t('approval.batchApproveConfirm')}
            </Text>

            {/* Summary of what will be approved */}
            <View style={styles.modalSummary}>
              {activeTypes.map((config) => (
                <View key={config.key} style={[styles.modalSummaryRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                  <View style={[styles.modalDot, { backgroundColor: config.color }]} />
                  <Text style={[styles.modalSummaryText, { color: themeColors.textPrimary }]}>
                    {isRTL ? config.labelAr : config.labelEn}
                  </Text>
                  <Text style={[styles.modalSummaryCount, { color: themeColors.textMuted }]}>
                    ({pendingCounts[config.key]})
                  </Text>
                </View>
              ))}
            </View>

            <View style={[styles.modalActions, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
              <Button
                variant="secondary"
                onPress={() => setShowConfirmModal(false)}
                style={{ flex: 1 }}
              >
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                variant="primary"
                onPress={handleBatchApprove}
                style={{ flex: 1 }}
              >
                {t('approval.batchApprove')}
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
    backgroundColor: tokens.colors.raw.white,
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["200"],
  },
  backButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.radii.absolute["radius-10"],
    backgroundColor: tokens.colors.raw.gray["100"],
  },
  headerContent: {
    flex: 1,
    marginEnd: tokens.spacing.absolute["3"],
  },
  headerTitle: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.bold,
    color: tokens.colors.raw.gray["900"],
  },
  headerSubtitle: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    marginTop: tokens.spacing.absolute["0_5"],
  },
  successBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#DCFCE7',
    paddingVertical: tokens.spacing.absolute["2_5"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
  },
  successText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: '#16A34A',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: tokens.spacing.absolute["4"],
    paddingBottom: 100,
  },
  typeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: tokens.spacing.absolute["4"],
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    marginBottom: tokens.spacing.absolute["2_5"],
    gap: tokens.spacing.absolute["3"],
  },
  typeIconContainer: {
    width: 44,
    height: 44,
    borderRadius: tokens.radii.absolute["radius-10"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  typeInfo: {
    flex: 1,
  },
  typeLabel: {
    fontSize: 15,
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["900"],
  },
  typeSubLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    marginTop: tokens.spacing.absolute["0_5"],
  },
  countBadge: {
    minWidth: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: tokens.spacing.absolute["2_5"],
  },
  countText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '700',
  },
  totalCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacing.absolute["4"],
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    marginTop: tokens.spacing.absolute["1_5"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  totalLabel: {
    fontSize: 15,
    fontFamily: FONTS.medium,
  },
  totalCount: {
    fontSize: tokens.typography["heading 3"]["font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '700',
  },
  bottomBar: {
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
    borderTopWidth: tokens.spacing.absolute["px"],
    backgroundColor: tokens.colors.raw.white,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing.absolute["6"],
  },
  modalContent: {
    width: '100%',
    maxWidth: 420,
    borderRadius: tokens.radii.absolute["radius-16"],
    padding: tokens.spacing.absolute["6"],
  },
  modalTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.bold,
    marginBottom: tokens.spacing.absolute["2"],
  },
  modalMessage: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    lineHeight: 22,
    marginBottom: tokens.spacing.absolute["4"],
  },
  modalSummary: {
    backgroundColor: tokens.colors.raw.gray["50"],
    borderRadius: tokens.radii.absolute["radius-10"],
    padding: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["5"],
    gap: tokens.spacing.absolute["2"],
  },
  modalSummaryRow: {
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  modalDot: {
    width: 8,
    height: 8,
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  modalSummaryText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    flex: 1,
  },
  modalSummaryCount: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.numbers,
  },
  modalActions: {
    gap: tokens.spacing.absolute["2_5"],
  },
  // Deletion Requests Section
  deletionSection: {
    marginTop: tokens.spacing.absolute["6"],
    gap: tokens.spacing.absolute["3"],
  },
  deletionSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.bold,
    color: tokens.colors.raw.gray["900"],
  },
  deletionSectionSubtitle: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    marginBottom: tokens.spacing.absolute["1"],
  },
  deletionCard: {
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    padding: tokens.spacing.absolute["3_5"],
    gap: tokens.spacing.absolute["3"],
  },
  deletionCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  deletionIconContainer: {
    width: 36,
    height: 36,
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: '#FEE2E2',
    alignItems: 'center',
    justifyContent: 'center',
  },
  deletionCardInfo: {
    flex: 1,
  },
  deletionCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["900"],
  },
  deletionCardMeta: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    marginTop: tokens.spacing.absolute["0_5"],
  },
  deletionCardActions: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["2"],
    justifyContent: 'flex-end',
  },
});

const styles = getStyles(true);
